import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { DateProposer } from "@/components/shared/DateProposer";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { ItemDetailPopover } from "@/components/shared/ItemDetailPopover";
import { showToast } from "@/components/shared/Toast";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { SessionComments } from "@/components/session/SessionComments";
import { SessionPostmortem } from "@/components/session/SessionPostmortem";
import {
  ArrowLeft, Calendar, Lock, Gift, Sparkles, Check, ChevronRight, ChevronDown,
  CheckCircle2, CheckCircle, ChevronUp, Pencil,
} from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";

interface RawComment {
  id: string;
  author: "admin" | "client";
  encrypted_text: string;
  nonce: string;
  is_image: boolean;
  created_at: string;
}

interface Review {
  emoji?: string;
  encrypted_feedback?: string;
  nonce?: string;
  submitted_at?: string;
}

interface RedeemedItem {
  _key: string;
  edge_key: string;
  name: string;
  icon: string;
  points: number;
  parent_key?: string | null;
  type: "punishment" | "reward";
  item_type?: "addon" | "scene" | null;
  description?: string;
  redeemed_at: string;
}

interface SessionData {
  _key: string;
  name: string;
  icon: string;
  description: string;
  proposed_dates: string[];
  fixed_date: string | null;
  points: number | null;
  current_points: number;
  created_at: string;
  completed_at?: string | null;
  redeemed: RedeemedItem[];
  comments: RawComment[];
  admin_review: Review | null;
  client_review: Review | null;
}

interface Reward {
  _key: string;
  name: string;
  icon: string;
  description?: string;
  points: number;
  parent_key?: string | null;
  related_keys?: string[];
  included_keys?: string[];
  item_type?: "addon" | "scene" | null;
}

interface TreeNode {
  item: Reward;
  children: TreeNode[];
}

function buildDeepTree(items: Reward[]): TreeNode[] {
  const itemMap = new Map(items.map(i => [i._key, i]));
  const childMap = new Map<string, TreeNode[]>();
  for (const item of items) {
    if (item.parent_key && itemMap.has(item.parent_key)) {
      const siblings = childMap.get(item.parent_key) || [];
      siblings.push({ item, children: [] });
      childMap.set(item.parent_key, siblings);
    }
  }
  function attachChildren(nodes: TreeNode[]) {
    for (const node of nodes) {
      node.children = childMap.get(node.item._key) || [];
      attachChildren(node.children);
    }
  }
  const roots: TreeNode[] = [];
  for (const item of items) {
    if (!item.parent_key || !itemMap.has(item.parent_key)) roots.push({ item, children: [] });
  }
  attachChildren(roots);
  return roots;
}

function areAllAncestorsSelected(itemKey: string, allItems: Map<string, Reward>, selectedKeys: Set<string>): boolean {
  const item = allItems.get(itemKey);
  if (!item) return true;
  let cur = item.parent_key;
  while (cur) {
    if (!selectedKeys.has(cur)) return false;
    cur = allItems.get(cur)?.parent_key || null;
  }
  return true;
}

function RewardNode({
  node, depth, selectedKeys, allItems, includedByKeys, collapsedParents, toggleCollapsed,
  onToggle, onSelectRelated, canAfford, commitInProgress,
}: {
  node: TreeNode; depth: number; selectedKeys: Set<string>; allItems: Map<string, Reward>;
  includedByKeys: Set<string>; collapsedParents: Set<string>; toggleCollapsed: (key: string) => void; onToggle: (key: string) => void;
  onSelectRelated: (key: string) => void; canAfford: (key: string) => boolean; commitInProgress: boolean;
}) {
  const r = node.item;
  const isSelected = selectedKeys.has(r._key);
  const isIncluded = includedByKeys.has(r._key);
  const isLocked = depth > 0 && !areAllAncestorsSelected(r._key, allItems, selectedKeys);
  const isCollapsed = collapsedParents.has(r._key);
  const hasChildren = node.children.length > 0;
  const affordable = canAfford(r._key);
  const canToggle = !isLocked && !commitInProgress && (isSelected || affordable);
  const marginLeft = depth > 0 ? `${Math.min(depth * 1.5, 6)}rem` : undefined;

  return (
    <div>
      <div style={{ marginLeft }} onClick={() => canToggle && onToggle(r._key)}
        className={`${depth > 0 ? "mt-1" : ""} flex items-center gap-2.5 p-3 rounded-xl border transition-all duration-200 ${
          isLocked ? "border-app-border/50 bg-app-surface-raised/30 opacity-40 cursor-not-allowed"
            : !canToggle ? "border-app-border/50 bg-app-surface-raised/30 opacity-60 cursor-not-allowed"
            : isSelected ? "border-app-success/30 bg-app-success/5 cursor-pointer"
            : "border-app-border hover:border-app-success/30 hover:bg-app-success/5 hover:shadow-sm cursor-pointer"
        }`}
        title={isLocked ? "Select ancestor first" : !affordable && !isSelected ? "Not enough points" : undefined}>
        {hasChildren && (
          <button type="button" onClick={(e) => { e.stopPropagation(); toggleCollapsed(r._key); }}
            className="w-5 h-5 flex items-center justify-center text-app-text-muted hover:text-app-text transition-colors">
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
          isSelected ? "bg-app-success border-app-success text-white" : "border-app-border"
        }`}>{isSelected && <Check className="w-3 h-3" />}</div>
        <div className={`${depth > 0 ? "w-8 h-8 text-base" : "w-9 h-9 text-lg"} rounded-xl flex items-center justify-center ${
          isSelected ? "bg-app-success/20" : r.item_type === "scene" ? "bg-app-info/10" : r.item_type === "addon" ? "bg-app-warning/10" : "bg-app-success/10"
        }`}>{r.icon}</div>
        <div className="flex-1 min-w-0">
          <p className={`text-body font-medium ${isSelected ? "text-app-success" : ""}`}>{r.name}</p>
          {r.description && (
            <ItemDetailPopover item={r} pointsColor="success" pointsPrefix="-"
              actions={[
                { label: "Add to Session", onClick: () => onToggle(r._key) },
              ]}>
              <MarkdownText text={r.description} className="text-info text-app-text-muted truncate" plain truncateAtSplit />
            </ItemDetailPopover>
          )}
          <p className="text-info font-semibold text-app-success">
            {isSelected ? (
              isIncluded ? <><span className="line-through opacity-50">-{r.points}</span>{" "}✓ Included (free)</> : "✓ Selected"
            ) : isLocked ? (
              "🔒 Locked"
            ) : !affordable ? (
              isIncluded ? <><span className="line-through opacity-50">{r.points}</span>{" "}⚠ 0 pts</> : `⚠ ${r.points} pts`
            ) : isIncluded ? (
              <><span className="line-through opacity-50">-{r.points}</span>{" "}-0 pts (included)</>
            ) : (
              `-${r.points} pts`
            )}
          </p>
          {r.item_type && (
            <span className={`inline-block text-info px-1.5 py-0 rounded-full font-medium mt-0.5 ${r.item_type === "scene" ? "bg-app-info/10 text-app-info" : "bg-app-warning/10 text-app-warning"}`}>
              {r.item_type === "scene" ? "🎬 Scene" : "🧩 Addon"}
            </span>
          )}
        </div>
      </div>
      {isSelected && r.related_keys && r.related_keys.length > 0 && (() => {
        const relatedItems = r.related_keys!.map(k => allItems.get(k)).filter((rel): rel is Reward => rel != null && !selectedKeys.has(rel._key));
        if (relatedItems.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1 mt-1.5" style={{ marginLeft }}>
            {relatedItems.map(rel => {
              const relIncluded = includedByKeys.has(rel._key);
              let totalCost = relIncluded ? 0 : rel.points; let cur = rel.parent_key;
              while (cur) { if (!selectedKeys.has(cur)) { const p = allItems.get(cur); if (p) totalCost += (includedByKeys.has(cur) ? 0 : p.points); } cur = allItems.get(cur)?.parent_key || null; }
              return (
                <ItemDetailPopover key={rel._key} item={rel} pointsColor="success" pointsPrefix="-"
                  actions={[
                    { label: "Add to Session", onClick: () => onSelectRelated(rel._key) },
                  ]}>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-app-success/10 hover:bg-app-success/20 text-app-success text-info font-medium transition-colors cursor-pointer disabled:opacity-50"
                    title={`Select ${rel.name} (-${totalCost} pts total)`}>
                    <span className="text-xs">{rel.icon}</span>
                    <span className="truncate max-w-20 text-xs">{rel.name}</span>
                    {relIncluded ? (
                      <><span className="text-xs opacity-50 line-through">-{rel.points}</span><span className="text-xs opacity-70">-0</span></>
                    ) : (
                      <span className="text-xs opacity-70">-{totalCost}</span>
                    )}
                  </span>
                </ItemDetailPopover>
              );
            })}
          </div>
        );
      })()}
      {isSelected && r.included_keys && r.included_keys.length > 0 && (() => {
        const includedItems = r.included_keys!.map(k => allItems.get(k)).filter((rel): rel is Reward => rel != null);
        if (includedItems.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1 mt-1" style={{ marginLeft }}>
            {includedItems.map(inc => (
              <ItemDetailPopover key={inc._key} item={inc} pointsColor="success" pointsPrefix="-"
                actions={[
                  { label: "Add to Session", onClick: () => onSelectRelated(inc._key) },
                ]}>
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-app-success/10 hover:bg-app-success/20 text-app-success text-info font-medium transition-colors cursor-pointer disabled:opacity-50"
                  title={`Included free: ${inc.name} (${inc.points} pts)`}>
                  <span className="text-xs">🎁</span>
                  <span className="text-xs">{inc.icon}</span>
                  <span className="truncate max-w-20 text-xs">{inc.name}</span>
                  <span className="text-xs opacity-50 line-through">-{inc.points}</span>
                  <span className="text-xs opacity-70">-0</span>
                </span>
              </ItemDetailPopover>
            ))}
          </div>
        );
      })()}
      {hasChildren && !isCollapsed && node.children.map(child => (
        <RewardNode key={child.item._key} node={child} depth={depth + 1} selectedKeys={selectedKeys}
          allItems={allItems} includedByKeys={includedByKeys} collapsedParents={collapsedParents} toggleCollapsed={toggleCollapsed}
          onToggle={onToggle} onSelectRelated={onSelectRelated} canAfford={canAfford} commitInProgress={commitInProgress} />
      ))}
    </div>
  );
}

function formatDisplayDate(dateStr: string): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export function ClientSessionDetail() {
  const { jwt, sessionId } = useParams();
  const navigate = useNavigate();
  const { getHeaders, user } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { decryptFields, decryptList, encryptValue, decryptValue, encryptBinary, decryptBinary, hasKey } = useEncryption(user?._key);

  const { data: rawSession, loading, error, refetch } = useApi<SessionData>(`/api/client/${jwt}/sessions/${sessionId}`);
  const { data: rawRewards } = useApi<Reward[]>(`/api/client/${jwt}/rewards`);

  const [session, setSession] = useState<SessionData | null>(null);
  const [rewards, setRewards] = useState<Reward[] | null>(null);
  const [proposeLoading, setProposeLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  const [itemsCollapsed, setItemsCollapsed] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const toggleCollapsed = (key: string) => {
    setCollapsedParents(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  useEffect(() => {
    if (!rawSession) return;
    (async () => {
      const dec = await decryptFields(rawSession as unknown as Record<string, unknown>, ["name", "description"]);
      const redeemed = rawSession.redeemed
        ? await decryptList(rawSession.redeemed as unknown as Record<string, unknown>[], ["name", "description"])
        : [];
      setSession({
        ...(dec as unknown as SessionData),
        redeemed: redeemed as unknown as RedeemedItem[],
        comments: rawSession.comments || [],
        admin_review: rawSession.admin_review,
        client_review: rawSession.client_review,
      });
    })();
  }, [rawSession, decryptFields, decryptList]);

  useEffect(() => {
    if (!rawRewards) return;
    decryptList(rawRewards as unknown as Record<string, unknown>[], ["name", "description"])
      .then(d => setRewards(d as unknown as Reward[]));
  }, [rawRewards, decryptList]);

  useEffect(() => {
    if (session?.redeemed) setSelectedKeys(new Set(session.redeemed.filter(r => r.type === "reward").map(r => r._key)));
  }, [session?.redeemed]);

  useEffect(() => { if (session?.completed_at) setItemsCollapsed(true); }, [session?.completed_at]);

  useEffect(() => {
    if (!rewards) return;
    const hasSelectedDescendant = (key: string): boolean => {
      for (const r of rewards) { if (r.parent_key === key && (selectedKeys.has(r._key) || hasSelectedDescendant(r._key))) return true; }
      return false;
    };
    const parentKeys = new Set(rewards.filter(r => r.parent_key).map(r => r.parent_key!));
    const toCollapse = new Set<string>();
    for (const key of parentKeys) { if (!selectedKeys.has(key) && !hasSelectedDescendant(key)) toCollapse.add(key); }
    setCollapsedParents(toCollapse);
  }, [rewards, selectedKeys]);

  const redeemedKeys = new Set(session?.redeemed?.filter(r => r.type === "reward").map(r => r._key) || []);
  const rewardMap = new Map(rewards?.map(r => [r._key, r]) || []);
  const rewardTree = rewards ? buildDeepTree(rewards) : [];
  const currentPoints = session?.current_points ?? 0;

  // Compute which keys are "included" (price zeroed) by any currently selected item
  const includedByKeys = useMemo(() => {
    const included = new Set<string>();
    for (const key of selectedKeys) {
      const item = rewardMap.get(key);
      if (item?.included_keys) { for (const ik of item.included_keys) included.add(ik); }
    }
    return included;
  }, [selectedKeys, rewardMap]);

  const newlySelected = [...selectedKeys].filter(k => !redeemedKeys.has(k));
  const newlyDeselected = [...redeemedKeys].filter(k => !selectedKeys.has(k));
  const pendingPointChange = -newlySelected.reduce((sum, k) => sum + (includedByKeys.has(k) ? 0 : (rewardMap.get(k)?.points || 0)), 0)
    + newlyDeselected.reduce((sum, k) => sum + (rewardMap.get(k)?.points || 0), 0);
  const previewBalance = currentPoints + pendingPointChange;
  const hasChanges = newlySelected.length > 0 || newlyDeselected.length > 0;

  // Calculate savings from included items
  const includedSavings = useMemo(() => {
    let savings = 0;
    for (const key of selectedKeys) {
      if (includedByKeys.has(key)) {
        savings += rewardMap.get(key)?.points || 0;
      }
    }
    return savings;
  }, [selectedKeys, includedByKeys, rewardMap]);

  const canAffordReward = useCallback((key: string): boolean => {
    if (selectedKeys.has(key)) return true;
    const pts = includedByKeys.has(key) ? 0 : (rewardMap.get(key)?.points || 0);
    return previewBalance - pts >= 0;
  }, [selectedKeys, rewardMap, previewBalance, includedByKeys]);

  // Compute which committed items were "included" (free) by another committed item
  const allRedeemed = useMemo(() => (session?.redeemed || []).filter(r => r.type === "reward" && r.name), [session?.redeemed]);
  const committedIncludedKeys = useMemo(() => {
    const included = new Set<string>();
    const redeemedKeySet = new Set(allRedeemed.map(r => r._key));
    for (const key of redeemedKeySet) {
      const item = rewardMap.get(key);
      if (item?.included_keys) { for (const ik of item.included_keys) { if (redeemedKeySet.has(ik)) included.add(ik); } }
    }
    return included;
  }, [allRedeemed, rewardMap]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;
  if (!session) return <EmptyState icon="📅" title="Session not found" />;

  const toggleSelection = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        const removeDesc = (pk: string) => { for (const r of (rewards || [])) { if (r.parent_key === pk && next.has(r._key)) { next.delete(r._key); removeDesc(r._key); } } };
        removeDesc(key);
      } else { next.add(key); }
      return next;
    });
  };

  const selectWithParents = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.add(key);
      let cur = rewardMap.get(key)?.parent_key;
      while (cur) { next.add(cur); cur = rewardMap.get(cur)?.parent_key || null; }
      return next;
    });
  };

  const handleCommit = async () => {
    if (!hasChanges) return;
    setCommitLoading(true);
    try {
      const unredeemEdgeKeys = newlyDeselected.map(k => session.redeemed?.find(r => r._key === k)?.edge_key).filter(Boolean) as string[];
      const res = await fetch(`/api/client/${jwt}/sessions/${sessionId}/batch-update`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ redeem_keys: newlySelected, unredeem_edge_keys: unredeemEdgeKeys }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Rewards updated!");
      setEditMode(false);
      refetch();
    } catch (err) { showToast.error(err instanceof Error ? err.message : "Failed to update"); }
    finally { setCommitLoading(false); }
  };

  const handlePropose = async (dates: string[]) => {
    setProposeLoading(true);
    try {
      const res = await fetch(`/api/client/${jwt}/sessions/${sessionId}/propose`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify({ dates }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Dates proposed!");
      refetch();
    } catch (err) { showToast.error(err instanceof Error ? err.message : "Failed to propose dates"); }
    finally { setProposeLoading(false); }
  };

  const handleSendComment = async (encrypted_text: string) => {
    const res = await fetch(`/api/client/${jwt}/sessions/${sessionId}/comment`, {
      method: "POST", headers: getHeaders(), body: JSON.stringify({ encrypted_text, nonce: "embedded" }),
    });
    if (!res.ok) throw new Error((await res.json()).error);
    refetch();
  };

  const handleSendImageComment = async (encrypted_text: string) => {
    const res = await fetch(`/api/client/${jwt}/sessions/${sessionId}/comment`, {
      method: "POST", headers: getHeaders(), body: JSON.stringify({ encrypted_text, nonce: "embedded", is_image: true }),
    });
    if (!res.ok) throw new Error((await res.json()).error);
    refetch();
  };

  const handleSubmitReview = async (emoji: string, encrypted_feedback: string) => {
    const res = await fetch(`/api/client/${jwt}/sessions/${sessionId}/review`, {
      method: "POST", headers: getHeaders(), body: JSON.stringify({ emoji, encrypted_feedback }),
    });
    if (!res.ok) throw new Error((await res.json()).error);
    showToast.success("Review submitted!");
    refetch();
  };

  const scenes = allRedeemed.filter(r => r.item_type === "scene");
  const addons = allRedeemed.filter(r => r.item_type === "addon");
  const others = allRedeemed.filter(r => !r.item_type);
  const totalItems = allRedeemed.length;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-xl hover:bg-app-surface-raised">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-app-primary/15 flex items-center justify-center text-xl shrink-0">{session.icon}</div>
          <div className="flex-1 min-w-0">
            <h1 className="text-heading font-bold leading-tight">{session.name}</h1>
            {session.description && <MarkdownText text={session.description} className="text-info text-app-text-muted" />}
            {session.completed_at && (
              <span className="inline-flex items-center gap-1 text-info bg-app-success/15 text-app-success px-2 py-0.5 rounded-full mt-0.5">
                <CheckCircle className="w-3 h-3" /> Completed
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* ── Left Column ── */}
        <div className="space-y-4">
          {/* Date Card */}
          <Card className="border-app-border rounded-2xl overflow-hidden pt-0">
            <CardHeader className="pb-2 bg-app-primary/5 rounded-t-2xl pt-4">
              <CardTitle className="text-body flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-app-primary/15 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-app-primary" />
                </div>
                <span>Session Date</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {session.fixed_date ? (
                <>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-app-success/10 border border-app-success/20">
                    <div className="w-8 h-8 rounded-full bg-app-success/20 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-app-success" />
                    </div>
                    <div className="flex-1">
                      <p className="text-body font-bold text-app-success">{formatDisplayDate(session.fixed_date)}</p>
                      <p className="text-info text-app-text-muted">Date is locked</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-app-surface-raised">
                    <Sparkles className="w-4 h-4 text-app-primary" />
                    <span className="text-info text-app-text-muted">Budget at lock:</span>
                    <span className="text-body font-bold text-app-primary">{session.points}</span>
                  </div>
                  <div className={`flex items-center gap-2 p-2.5 rounded-xl ${currentPoints >= 0 ? "bg-app-success/10" : "bg-app-danger/10"}`}>
                    <span className="text-info text-app-text-muted">Your balance:</span>
                    <span className={`text-body font-bold ${currentPoints >= 0 ? "text-app-success" : "text-app-danger"}`}>{currentPoints} pts</span>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <DateProposer proposedDates={session.proposed_dates} onPropose={handlePropose} disabled={proposeLoading} />
                  <div className="flex items-start gap-2 p-2.5 rounded-xl bg-app-info/10 border border-app-primary/10">
                    <span className="text-sm">💡</span>
                    <p className="text-info text-app-text-muted">Propose dates you're available. Your admin will pick and lock one.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Session Comments */}
          <SessionComments
            comments={session.comments || []}
            currentRole="client"
            encryptValue={encryptValue}
            decryptValue={decryptValue}
            encryptBinary={encryptBinary}
            decryptBinary={decryptBinary}
            hasKey={hasKey}
            onSendText={handleSendComment}
            onSendImage={handleSendImageComment}
          />

          {/* Postmortem */}
          {session.completed_at && (
            <SessionPostmortem
              ownReview={session.client_review}
              partnerReview={session.admin_review}
              ownRole="client"
              encryptValue={encryptValue}
              decryptValue={decryptValue}
              hasKey={hasKey}
              onSubmit={handleSubmitReview}
            />
          )}
        </div>

        {/* ── Right Column ── */}
        <div className="space-y-4">
          {editMode ? (
            /* Reward Selector */
            <Card className="border-app-success/20 rounded-2xl overflow-hidden pt-0">
              <CardHeader className="pb-2 bg-app-success/5 rounded-t-2xl pt-4">
                <CardTitle className="text-body flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-app-success/15 flex items-center justify-center">
                    <Gift className="w-4 h-4 text-app-success" />
                  </div>
                  <span>Select Rewards</span>
                  <span className="ml-auto text-info bg-app-success/10 px-2 py-0.5 rounded-full text-app-success">{rewards?.length || 0}</span>
                  <Button size="sm" variant="outline" className="ml-2"
                    onClick={() => { setEditMode(false); setSelectedKeys(new Set(session.redeemed.filter(r => r.type === "reward").map(r => r._key))); }}>
                    Cancel
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <div className={`flex items-center justify-between p-3 rounded-xl mb-3 ${previewBalance === currentPoints ? "bg-app-surface-raised" : "bg-app-warning/10 border border-app-warning/20"}`}>
                  <div>
                    <p className="text-info text-app-text-muted">Balance after changes</p>
                    <p className={`text-lg font-bold ${previewBalance >= 0 ? "text-app-success" : "text-app-danger"}`}>{previewBalance} pts</p>
                  </div>
                  {pendingPointChange !== 0 && (
                    <span className={`text-info font-semibold px-2 py-0.5 rounded-full ${pendingPointChange > 0 ? "text-app-success bg-app-success/10" : "text-app-danger bg-app-danger/10"}`}>
                      {pendingPointChange > 0 ? "+" : ""}{pendingPointChange} pts
                    </span>
                  )}
                </div>
                {rewardTree.length > 0 ? (
                  <div className="space-y-2">
                    {rewardTree.map(node => (
                      <RewardNode key={node.item._key} node={node} depth={0} selectedKeys={selectedKeys}
                        allItems={rewardMap} includedByKeys={includedByKeys} collapsedParents={collapsedParents} toggleCollapsed={toggleCollapsed}
                        onToggle={toggleSelection} onSelectRelated={selectWithParents}
                        canAfford={canAffordReward} commitInProgress={commitLoading} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-3xl mb-2">✅</p>
                    <p className="text-info text-app-text-muted">No rewards configured</p>
                  </div>
                )}
                {includedSavings > 0 && (
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-app-success/10 border border-app-success/20 mt-3">
                    <span className="text-info text-app-text-muted">Included items savings</span>
                    <span className="text-info font-semibold text-app-success">
                      <span className="line-through opacity-50">-{includedSavings}</span> → -0 pts
                    </span>
                  </div>
                )}
                {hasChanges && (
                  <div className="mt-4 pt-3 border-t border-app-border flex items-center justify-between">
                    <div className="text-info text-app-text-muted">
                      {newlySelected.length > 0 && <span className="text-app-success">+{newlySelected.length} new</span>}
                      {newlySelected.length > 0 && newlyDeselected.length > 0 && <span> · </span>}
                      {newlyDeselected.length > 0 && <span className="text-app-text-muted">-{newlyDeselected.length} removed</span>}
                    </div>
                    <Button onClick={handleCommit} disabled={commitLoading} className="bg-app-success hover:bg-app-success/90 text-white rounded-xl">
                      {commitLoading ? "Saving…" : "Confirm Selection"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Committed Items */
            <Card className="border-app-border rounded-2xl overflow-hidden pt-0">
              <CardHeader className="pb-2 bg-app-surface-raised/50 rounded-t-2xl pt-4">
                <CardTitle className="text-body flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-app-accent/15 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-app-accent" />
                  </div>
                  <span>Your Rewards</span>
                  <span className="ml-auto text-info bg-app-surface-raised px-2 py-0.5 rounded-full text-app-text-muted">{totalItems}</span>
                  {session.fixed_date && !session.completed_at && (
                    <Button size="sm" variant="outline" onClick={() => setEditMode(true)}
                      className="ml-2 border-app-success text-app-success hover:bg-app-success/10">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                  )}
                  {session.completed_at && (
                    <button type="button" onClick={() => setItemsCollapsed(!itemsCollapsed)}
                      className="ml-2 w-7 h-7 rounded-lg flex items-center justify-center text-app-text-muted hover:bg-app-surface-raised transition-colors">
                      {itemsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                    </button>
                  )}
                </CardTitle>
              </CardHeader>

              {!itemsCollapsed && (
                <CardContent className="pt-4 space-y-4">
                  {totalItems === 0 && (
                    <div className="text-center py-6">
                      <p className="text-3xl mb-2">🎁</p>
                      <p className="text-info text-app-text-muted">
                        {session.fixed_date ? "No rewards selected yet." : "Rewards unlock after the date is fixed."}
                      </p>
                      {session.fixed_date && !session.completed_at && currentPoints > 0 && (
                        <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="mt-3 border-app-success text-app-success">
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Select Rewards
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Categories */}
                  {others.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-app-text-muted uppercase tracking-wide px-1 mb-2">🎁 Categories</p>
                      <div className="flex flex-wrap gap-1.5">
                        {others.map(item => (
                          <span key={item.edge_key} className="inline-flex items-center gap-1 text-[12px] font-medium px-2 py-1 rounded-full border border-app-border bg-app-surface-raised/50">
                            <span>{item.icon}</span>
                            <span>{item.name}</span>
                            {committedIncludedKeys.has(item._key) ? (
                              <><span className="text-app-success font-semibold line-through opacity-50">{item.points}p</span><span className="text-app-success font-semibold">0p</span></>
                            ) : (
                              <span className="text-app-success font-semibold">{item.points}p</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Scenes */}
                  {scenes.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-[11px] font-semibold text-app-text-muted uppercase tracking-wide px-1">🎬 Scenes</p>
                      {scenes.map(item => (
                        <div key={item.edge_key} className="rounded-2xl border border-app-info/20 bg-app-info/5 overflow-hidden">
                          <div className="flex items-center gap-3 p-3 pb-2">
                            <div className="w-10 h-10 rounded-xl bg-app-info/15 flex items-center justify-center text-xl shrink-0">{item.icon}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-body font-semibold">{item.name}</span>
                                <span className="text-[11px] px-1.5 py-0 rounded-full font-medium bg-app-info/15 text-app-info">🎬 Scene</span>
                              </div>
                              <span className="text-info font-semibold text-app-success">
                                {committedIncludedKeys.has(item._key) ? (
                                  <><span className="line-through opacity-50">-{item.points}</span> -0 pts</>
                                ) : (
                                  <>-{item.points} pts</>
                                )}
                              </span>
                            </div>
                          </div>
                          {item.description && (
                            <div className="px-4 pb-3">
                              <MarkdownText text={item.description} className="text-info text-app-text-muted" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add-ons */}
                  {addons.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold text-app-text-muted uppercase tracking-wide px-1">🧩 Add-ons</p>
                      {addons.map(item => (
                        <div key={item.edge_key} className="rounded-xl border border-app-accent/20 bg-app-accent/5 p-3 space-y-1.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-app-accent/15 flex items-center justify-center text-lg shrink-0">{item.icon}</div>
                            <div className="flex-1 min-w-0">
                              <span className="text-body font-semibold">{item.name}</span>
                              <span className="text-info font-semibold text-app-success block">
                                {committedIncludedKeys.has(item._key) ? (
                                  <><span className="line-through opacity-50">-{item.points}</span> -0 pts</>
                                ) : (
                                  <>-{item.points} pts</>
                                )}
                              </span>
                            </div>
                          </div>
                          {item.description && <MarkdownText text={item.description} className="text-info text-app-text-muted pl-1" />}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}
        </div>
      </div>

      {confirmDialog}
    </div>
  );
}
